// src/lib/businessDays.ts

/**
 * Business days utility for calculating working days between dates,
 * accounting for weekends and public holidays using Nager.Date API
 */

interface PublicHoliday {
  date: string;
  localName: string;
  name: string;
  countryCode: string;
  global: boolean;
  types: string[];
}

// Cache for holidays to avoid excessive API calls
const holidayCache: Map<string, Set<string>> = new Map();

/**
 * Fetches public holidays for a given year and country from Nager.Date API
 * @param year - The year to fetch holidays for
 * @param countryCode - ISO 3166-1 alpha-2 country code (default: US)
 * @returns Set of holiday dates in YYYY-MM-DD format
 */
export async function fetchPublicHolidays(
  year: number,
  countryCode: string = 'US'
): Promise<Set<string>> {
  const cacheKey = `${year}-${countryCode}`;
  
  // Return cached holidays if available
  const cached = holidayCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(
      `https://date.nager.at/api/v3/PublicHolidays/${year}/${countryCode}`
    );

    if (!response.ok) {
      console.warn(`⚠️ Failed to fetch holidays for ${year}: ${response.statusText}`);
      return new Set();
    }

    const holidays: PublicHoliday[] = await response.json();
    
    // Only include Public and Bank holidays (not Observance or Optional)
    const holidayDates = new Set(
      holidays
        .filter(h => h.types.includes('Public') || h.types.includes('Bank'))
        .map(h => h.date)
    );

    // Cache the results
    holidayCache.set(cacheKey, holidayDates);
    
    console.log(`✅ Loaded ${holidayDates.size} holidays for ${year} (${countryCode})`);
    
    return holidayDates;
  } catch (error) {
    console.error('❌ Error fetching holidays:', error);
    return new Set();
  }
}

/**
 * Checks if a date falls on a weekend (Saturday or Sunday)
 * @param date - Date string in YYYY-MM-DD format or Date object
 * @returns true if the date is a weekend
 */
export function isWeekend(date: string | Date): boolean {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  const dayOfWeek = dateObj.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6; // Sunday = 0, Saturday = 6
}

/**
 * Checks if a date is a public holiday
 * @param date - Date string in YYYY-MM-DD format
 * @param holidays - Set of holiday dates
 * @returns true if the date is a holiday
 */
export function isPublicHoliday(date: string, holidays: Set<string>): boolean {
  return holidays.has(date);
}

/**
 * Checks if a date is a working day (not weekend, not holiday)
 * @param date - Date string in YYYY-MM-DD format
 * @param holidays - Set of holiday dates
 * @returns true if the date is a working day
 */
export function isWorkingDay(date: string, holidays: Set<string>): boolean {
  return !isWeekend(date) && !isPublicHoliday(date, holidays);
}

/**
 * Gets the next working day after a given date
 * @param date - Date string in YYYY-MM-DD format
 * @param holidays - Set of holiday dates
 * @returns Next working day in YYYY-MM-DD format
 */
export function getNextWorkingDay(date: string, holidays: Set<string>): string {
  const currentDate = new Date(date);
  const maxIterations = 30; // Safety limit (handles long holiday periods)
  
  for (let i = 0; i < maxIterations; i++) {
    currentDate.setDate(currentDate.getDate() + 1);
    const dateStr = currentDate.toISOString().split('T')[0];
    
    if (isWorkingDay(dateStr, holidays)) {
      return dateStr;
    }
  }
  
  // Fallback: if no working day found in 30 days, return next day
  currentDate.setDate(currentDate.getDate() + 1);
  return currentDate.toISOString().split('T')[0];
}

/**
 * Gets the previous working day before a given date
 * @param date - Date string in YYYY-MM-DD format
 * @param holidays - Set of holiday dates
 * @returns Previous working day in YYYY-MM-DD format
 */
export function getPreviousWorkingDay(date: string, holidays: Set<string>): string {
  const currentDate = new Date(date);
  const maxIterations = 30; // Safety limit (handles long holiday periods)
  
  for (let i = 0; i < maxIterations; i++) {
    currentDate.setDate(currentDate.getDate() - 1);
    const dateStr = currentDate.toISOString().split('T')[0];
    
    if (isWorkingDay(dateStr, holidays)) {
      return dateStr;
    }
  }
  
  // Fallback: if no working day found in 30 days, return previous day
  currentDate.setDate(currentDate.getDate() - 1);
  return currentDate.toISOString().split('T')[0];
}

/**
 * Calculates the number of business days between two dates (exclusive of end date)
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @param holidays - Set of holiday dates
 * @returns Number of business days between the dates
 */
export function getBusinessDaysBetween(
  startDate: string,
  endDate: string,
  holidays: Set<string>
): number {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (start >= end) {
    return 0;
  }

  let businessDays = 0;
  const current = new Date(start);

  while (current < end) {
    const dateStr = current.toISOString().split('T')[0];
    
    if (isWorkingDay(dateStr, holidays)) {
      businessDays++;
    }
    
    current.setDate(current.getDate() + 1);
  }

  return businessDays;
}

/**
 * Checks if two dates are consecutive working days
 * @param date1 - First date in YYYY-MM-DD format
 * @param date2 - Second date in YYYY-MM-DD format (should be after date1)
 * @param holidays - Set of holiday dates
 * @returns true if date2 is the next working day after date1
 */
export function areConsecutiveWorkingDays(
  date1: string,
  date2: string,
  holidays: Set<string>
): boolean {
  const nextWorkingDay = getNextWorkingDay(date1, holidays);
  return nextWorkingDay === date2;
}

/**
 * Loads holidays for multiple years to ensure coverage
 * @param startYear - Starting year
 * @param endYear - Ending year
 * @param countryCode - ISO 3166-1 alpha-2 country code (default: US)
 * @returns Combined set of all holidays
 */
export async function loadHolidaysForYears(
  startYear: number,
  endYear: number,
  countryCode: string = 'US'
): Promise<Set<string>> {
  const allHolidays = new Set<string>();
  
  for (let year = startYear; year <= endYear; year++) {
    const yearHolidays = await fetchPublicHolidays(year, countryCode);
    yearHolidays.forEach(date => {
      allHolidays.add(date);
    });
  }
  
  return allHolidays;
}

/**
 * Checks if the streak should continue between two dates
 * Streak continues if:
 * - Same day (already tested today)
 * - Next working day
 * @param lastTestDate - Last test date in YYYY-MM-DD format
 * @param currentDate - Current test date in YYYY-MM-DD format
 * @param holidays - Set of holiday dates
 * @returns Object with { continues: boolean, businessDaysDiff: number }
 */
export function shouldContinueStreak(
  lastTestDate: string,
  currentDate: string,
  holidays: Set<string>
): { continues: boolean; businessDaysDiff: number } {
  // Same day - streak continues
  if (lastTestDate === currentDate) {
    return { continues: true, businessDaysDiff: 0 };
  }

  // Check if consecutive working days
  const isConsecutive = areConsecutiveWorkingDays(lastTestDate, currentDate, holidays);
  
  if (isConsecutive) {
    return { continues: true, businessDaysDiff: 1 };
  }

  // Calculate business days between
  const businessDays = getBusinessDaysBetween(lastTestDate, currentDate, holidays);
  
  return {
    continues: businessDays <= 1,
    businessDaysDiff: businessDays,
  };
}

